import React, { useEffect, useState } from 'react'
// eslint-disable-next-line no-restricted-imports
import { Modal, Form, Alert, Divider } from 'antd'
import { Input } from '@/ui'
import styles from './index.less'
import useRecordOperation from '@/hooks/useRecordOperation'
import getQuickSearchInitValues from '../../helper/getQuickSearchInitValues'
import getQuickSearchSubmitValues from '../../helper/getQuickSearchSubmitValues'
import { getSortDisplayValue } from '../../helper/getFieldDisplayValue'

const useStore = ({
    form,
    fieldsGroup,
    values,
    preInitValues,
    sortFields: inputSortFields,
    sortValues: inputSortValues
}: any) => {
    const [queryFields, setQueryFields] = useState([])
    const [sortFields, setSortFields] = useState([])
    const [sortValues, setSortValues] = useState(null)

    const [innerForm] = Form.useForm()

    // 初始化
    const init = () => {
        const { queryFields: newQueryFields, sortValues: newSortValues } = preInitValues
            ? preInitValues
            : getQuickSearchInitValues({
                  form,
                  fieldsGroup,
                  values,
                  sortFields: inputSortFields,
                  sortValues: inputSortValues
              })
        setQueryFields(newQueryFields)
        setSortFields(inputSortFields)
        setSortValues(newSortValues)
    }

    // 重置
    const reset = () => {
        setQueryFields([])
        setSortFields([])
        setSortValues(null)
    }

    return {
        innerForm,
        queryFields,
        sortFields,
        sortValues,
        setSortValues,
        init,
        reset
    }
}

const SortPreview = ({ fields, value, defaultSortText }: any) => {
    return (
        getSortDisplayValue({
            fields,
            value
        }) ||
        defaultSortText ||
        '未设置排序'
    )
}

const MAX_LEN = 10

const Content = ({ store, closeDialog, defaultSortText }) => {
    const { innerForm, queryFields, sortFields, sortValues } = store

    const { isRecord, saveRecordStatus } = useRecordOperation({
        name: 'quickSearchSortTip'
    })

    useEffect(() => {
        store.init()

        return () => {
            store.reset()
        }
    }, [])

    return (
        <Form form={innerForm} labelCol={{ flex: '72px' }}>
            <div className={styles.container}>
                <Form.Item
                    label={
                        <span className={styles.label} style={{ paddingRight: '7px' }}>
                            命名
                        </span>
                    }
                    name="quickSearchName"
                    rules={[
                        { required: true, message: '请输入保存筛选条件和排序的名称' },
                        {
                            validator: (_, value) => {
                                if (!!value && !value.trim()) {
                                    return Promise.reject(new Error('请输入保存筛选条件和排序的名称'))
                                }
                                if (!!value && value.trim().length > MAX_LEN) {
                                    return Promise.reject(new Error(`命名最长不得超过${MAX_LEN}个字`))
                                }
                                return Promise.resolve()
                            }
                        }
                    ]}
                    colon={false}
                    style={{ marginBottom: 0 }}
                >
                    <Input placeholder="请输入保存筛选条件和排序的名称" style={{ width: '406px' }} />
                </Form.Item>

                {!isRecord ? (
                    <Alert
                        className={styles.alert}
                        message={
                            <>
                                以下为筛选条件和排序预览，如需调整请返回上级页面修改
                                <Divider type="vertical" />
                                <a onClick={closeDialog}>去修改</a>
                            </>
                        }
                        type="info"
                        showIcon
                        closable
                        onClose={() => saveRecordStatus('1')}
                    />
                ) : null}

                <div className={styles.scroller}>
                    {queryFields.map(item => {
                        return (
                            <div key={item.groupKey || item.groupTitle} className={styles.item}>
                                <div className={styles.lt}>
                                    <span className={styles.label}>{item.groupTitle}</span>
                                </div>
                                <div className={styles.rt}>
                                    {item.fields.map(item2 => {
                                        return (
                                            <div key={item2.name} className={styles.field}>
                                                {item2.displayValue}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    })}
                    {sortFields?.length ? (
                        <div key="sort" className={styles.item}>
                            <div className={styles.lt}>
                                <span className={styles.label}>排序</span>
                            </div>
                            <div className={styles.rt}>
                                <SortPreview defaultSortText={defaultSortText} fields={sortFields} value={sortValues} />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </Form>
    )
}

const bodyStyles = {
    padding: '16px 24px'
}

export default ({
    visible,
    form,
    fieldsGroup,
    values,
    sortFields,
    sortValues,
    defaultSortText,
    onOk,
    onCancel,
    ...restProps
}: any) => {
    const [confirmLoading, setConfirmLoading] = useState(false)

    const store = useStore({ form, fieldsGroup, values, sortFields, sortValues })

    const handleOk = async () => {
        const { quickSearchName } = await store.innerForm.validateFields()

        setConfirmLoading(true)

        const submitValues = getQuickSearchSubmitValues({
            quickSearchName,
            queryFields: store.queryFields,
            sortFields: store.sortFields,
            sortValues: store.sortValues
        })

        await onOk(submitValues)

        setConfirmLoading(false)
    }

    return (
        <Modal
            visible={visible}
            title="保存筛选条件和排序"
            width={600}
            bodyStyle={bodyStyles}
            confirmLoading={confirmLoading}
            okText="保存筛选条件和排序"
            onOk={handleOk}
            onCancel={onCancel}
            {...restProps}
        >
            <Content store={store} defaultSortText={defaultSortText} closeDialog={onCancel} />
        </Modal>
    )
}
